import { Controller } from "@hotwired/stimulus";

// Runner d'audit single-page. Tous les groupes sont rendus dans le DOM ;
// on toggle leur visibilité quand l'utilisateur clique sur la TOC.
//
// À chaque changement de réponse, on POST vers /audit/answer avec :
//   question_id, scope_type, scope_id, value (ou value_json), note, attachment.
// Le serveur retourne progress[] (par groupe) + ready_to_complete (bool).
// On met à jour la TOC + le bouton "Valider l'audit".
export default class extends Controller {
  static targets = [
    "content", "group",
    "tocStateIcon", "tocProgress",
    "missing", "missingCount",
    "validateBtn", "reservesBtn",
    "question",
  ];
  static values = {
    answerUrl: String,
    completeUrl: String,
    completeWithReservesUrl: String,
    currentIndex: { type: Number, default: 0 },
  };

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    this._showGroup(this.currentIndexValue);
  }

  // ── TOC navigation ──────────────────────────────────────────────────

  openGroup(event) {
    const idx = parseInt(event.currentTarget.dataset.groupIndex, 10);
    this._showGroup(idx);
  }

  _showGroup(idx) {
    this.groupTargets.forEach(g => {
      const i = parseInt(g.dataset.groupIndex, 10);
      g.hidden = i !== idx;
    });
    this.currentIndexValue = idx;
    // Highlight active TOC item
    this.element.querySelectorAll(".aur-toc__item--active")
        .forEach(el => el.classList.remove("aur-toc__item--active"));
    const tocItem = this.element.querySelector(`.aur-toc__item[data-group-index="${idx}"]`);
    tocItem?.classList.add("aur-toc__item--active");
    // Scroll content top
    this.contentTarget.scrollTo?.({ top: 0, behavior: "smooth" });
  }

  // ── Answer handlers ─────────────────────────────────────────────────

  answer(event) {
    const el = event.currentTarget;
    const qid = el.dataset.questionId;
    const value = el.value;
    const scope = this._scopeFromQuestion(el);
    this._sendAnswer({ question_id: qid, value, ...scope });
  }

  answerMulti(event) {
    const el = event.currentTarget;
    const qid = el.dataset.questionId;
    const scope = this._scopeFromQuestion(el);
    // Collecte toutes les checkbox de la même question
    const root = el.closest(".aur-q__choices");
    const values = Array.from(root.querySelectorAll('input[type="checkbox"]'))
      .filter(c => c.checked).map(c => c.value);
    this._sendAnswer({
      question_id: qid,
      value_json: JSON.stringify(values),
      ...scope,
    });
  }

  answerNote(event) {
    const el = event.currentTarget;
    const qid = el.dataset.questionId;
    const note = el.value;
    const scope = this._scopeFromQuestion(el);
    this._sendAnswer({ question_id: qid, note, ...scope });
  }

  answerFile(event) {
    const el = event.currentTarget;
    const file = el.files?.[0];
    if (!file) return;
    const qid = el.dataset.questionId;
    const scope = {
      scope_type: el.dataset.scopeType,
      scope_id: el.dataset.scopeId,
    };
    const fd = new FormData();
    fd.append("question_id", qid);
    fd.append("scope_type", scope.scope_type);
    fd.append("scope_id", scope.scope_id);
    fd.append("attachment", file);
    this._postFormData(fd);
  }

  validate() {
    if (this.validateBtnTarget.disabled) return;
    if (!confirm("Valider l'audit comme terminé sans réserves ?")) return;
    this._post(this.completeUrlValue);
  }

  validateWithReserves() {
    if (this.reservesBtnTarget.disabled) return;
    if (!confirm("Clôturer l'audit avec réserves ? Tu pourras le réouvrir plus tard.")) return;
    this._post(this.completeWithReservesUrlValue);
  }

  // ── Network ─────────────────────────────────────────────────────────

  async _sendAnswer(data) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => v != null && fd.append(k, v));
    await this._postFormData(fd);
  }

  async _postFormData(fd) {
    try {
      const res = await fetch(this.answerUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this._csrf, "Accept": "application/json" },
        body: fd,
      });
      if (!res.ok) return;
      const payload = await res.json();
      this._refreshProgress(payload);
    } catch (_) { /* noop */ }
  }

  _post(url) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;
    form.style.display = "none";
    const csrf = document.createElement("input");
    csrf.type = "hidden";
    csrf.name = "authenticity_token";
    csrf.value = this._csrf;
    form.appendChild(csrf);
    const method = document.createElement("input");
    method.type = "hidden";
    method.name = "_method";
    method.value = "patch";
    form.appendChild(method);
    document.body.appendChild(form);
    form.submit();
  }

  // ── State refresh after answer ──────────────────────────────────────

  _refreshProgress(payload) {
    if (!payload?.progress) return;
    let totalRequiredMissing = 0;
    payload.progress.forEach(p => {
      // Met à jour le compteur dans la TOC
      const tocItem = this.element.querySelector(
        `.aur-toc__item[data-section-id="${p.section_id}"][data-scope-type="${p.scope_type}"][data-scope-id="${p.scope_id}"]`
      );
      if (!tocItem) return;
      const progressEl = tocItem.querySelector(".aur-toc__progress");
      const iconEl     = tocItem.querySelector(".aur-toc__state-icon");
      if (progressEl) progressEl.textContent = `${p.answered}/${p.total}`;
      // Met à jour la classe + icône d'état
      tocItem.classList.remove("aur-toc__item--ok", "aur-toc__item--partial", "aur-toc__item--todo");
      if (iconEl) iconEl.classList.remove("fa-circle-dashed", "fa-circle", "fa-circle-half-stroke", "fa-circle-check");

      if (p.total === 0) {
        tocItem.classList.add("aur-toc__item--ok");
        iconEl?.classList.add("fa-circle-dashed");
      } else if (p.answered === p.total && p.required_missing === 0) {
        tocItem.classList.add("aur-toc__item--ok");
        iconEl?.classList.add("fa-circle-check");
      } else if (p.answered > 0) {
        tocItem.classList.add("aur-toc__item--partial");
        iconEl?.classList.add("fa-circle-half-stroke");
      } else {
        tocItem.classList.add("aur-toc__item--todo");
        iconEl?.classList.add("fa-circle");
      }
      totalRequiredMissing += p.required_missing;
    });

    // Met à jour le résumé de fin
    if (this.hasMissingCountTarget) this.missingCountTarget.textContent = totalRequiredMissing;
    if (this.hasMissingTarget) this.missingTarget.hidden = totalRequiredMissing === 0;
    if (this.hasValidateBtnTarget) this.validateBtnTarget.disabled = !payload.ready_to_complete;
    if (this.hasReservesBtnTarget) this.reservesBtnTarget.disabled = !payload.ready_to_complete;
  }

  _scopeFromQuestion(el) {
    const q = el.closest(".aur-q");
    return {
      scope_type: q?.dataset.scopeType,
      scope_id:   q?.dataset.scopeId,
    };
  }
}
